import SignaturePad from 'signature_pad';

/**
 * signature_pad v2 guards `touchstart` against multi-touch (it only opens a
 * stroke when exactly one finger is down) but leaves `touchmove` and
 * `touchend` unguarded. So a second finger, a resting palm, or a `clear()`
 * that lands mid-gesture (our ResizeObserver does exactly that when a mobile
 * browser collapses its URL bar) leaves the move/end handlers driving a pad
 * with no open point group, and they blow up reading `_data[-1]`:
 *
 *   TypeError: Cannot read properties of undefined (reading 'push')   // _strokeUpdate
 *   TypeError: Cannot read properties of undefined (reading 'length') // _strokeEnd
 *
 * Upstream fixed this in v4 with an open-stroke flag consulted by every
 * handler. react-signature-canvas 1.0.x pins us to v2, so the same flag is
 * applied here until that dependency is replaced.
 */

type StrokeMethods = {
  _strokeBegin(this: PadState, event: unknown): void;
  _strokeUpdate(this: PadState, event: unknown): void;
  _strokeEnd(this: PadState, event: unknown): void;
  clear(this: PadState): void;
};

/**
 * The stroke lifecycle is private to signature_pad and absent from its type
 * declarations, so it is reached through a structural type rather than `any`.
 * `__strokeOpen` is ours; v2 has no equivalent.
 */
type PadState = StrokeMethods & { __strokeOpen?: boolean };

let installed = false;

/**
 * Patch the prototype rather than individual pads. react-signature-canvas is a
 * class component that builds its pad in componentDidMount, and it can rebuild
 * it without a ref or effect firing again (React StrictMode's dev remount does
 * this), so there is no reliable moment to catch every instance from outside.
 */
export function installSignaturePadGuard(): void {
  if (installed) return;
  installed = true;

  const proto = SignaturePad.prototype as unknown as StrokeMethods;
  const begin = proto._strokeBegin;
  const update = proto._strokeUpdate;
  const end = proto._strokeEnd;
  const clear = proto.clear;

  proto._strokeBegin = function (event) {
    // Set before delegating, because _strokeBegin calls _strokeUpdate itself
    // and that call has to get past the guard below.
    this.__strokeOpen = true;
    begin.call(this, event);
  };
  proto._strokeUpdate = function (event) {
    if (!this.__strokeOpen) return;
    update.call(this, event);
  };
  proto._strokeEnd = function (event) {
    if (!this.__strokeOpen) return;
    this.__strokeOpen = false;
    end.call(this, event);
  };
  proto.clear = function () {
    this.__strokeOpen = false;
    clear.call(this);
  };
}

// Installed on import, which has to happen before any pad is constructed: the
// constructor binds `_strokeMoveUpdate` to whatever `_strokeUpdate` is at that
// moment, so a later patch would never be seen by touchmove.
installSignaturePadGuard();
